import { KI_GRUND_MIN, KI_LOESUNG_ZUSTAND_LABELS } from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Markdown } from '../lib/markdown'
import { sitzungQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { hintergrundSchliesst } from './ui/hintergrund'
import { Button } from './ui/primitives'

/**
 * **🤖 KI-Lösung im Aufgabendetail** ([Z-21](../../../../docs/phasen/zusatz-21-claude-anbindung.md),
 * [E-412](../../../../docs/ENTSCHEIDUNGEN.md#e-412)).
 *
 * Freigeben, sehen, was Claude hinterlassen hat, und Claudes Kommentar-Entwürfe abschicken
 * oder verwerfen. Nur sichtbar mit dem Häkchen „Claude" an der eigenen Person – für alle
 * anderen gibt es diesen Abschnitt nicht.
 *
 * Nils, 16.10.2026: *„Kommentare die kunden sehen dürfen nie einfach so rausgehen."* Deshalb
 * steht an jedem Entwurf ausdrücklich, dass „Abschicken" ihn als **eigenen** Kommentar in die
 * Quelle schreibt.
 */
export function KiLoesung({ taskId }: { taskId: string }) {
  const { data: sitzung } = useQuery(sitzungQuery)
  const darf = Boolean(sitzung?.claudeZugang)
  const queryClient = useQueryClient()
  const schluessel = ['claude', 'stand', taskId]
  const { data: stand } = useQuery({
    queryKey: schluessel,
    queryFn: () => trpc.claude.stand.query({ taskId }),
    enabled: darf,
    staleTime: 15_000,
  })
  const [dialog, setDialog] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: schluessel })
    void queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] })
  }

  const zuruecknehmen = useMutation({
    mutationFn: () => trpc.claude.freigabeZuruecknehmen.mutate({ taskId }),
    onSuccess: neuLaden,
    onError: (e: Error) => setFehler(e.message),
  })

  if (!darf || !stand) return null
  const f = stand.freigabe

  if (!f) {
    return (
      <div className="mt-4">
        <Button variant="ghost" size="sm" onClick={() => setDialog(true)}>
          🤖 Für Claude freigeben
        </Button>
        {dialog && (
          <FreigabeDialog
            taskId={taskId}
            schliessen={() => setDialog(false)}
            fertig={() => {
              setDialog(false)
              neuLaden()
            }}
          />
        )}
      </div>
    )
  }

  return (
    <section
      aria-label="KI-Lösung"
      className="mt-4 rounded-card border border-line bg-[#f6f5ff] p-3 text-[12.5px]"
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <h4 className="text-xs font-bold">🤖 KI-Lösung</h4>
        <span className="rounded-full bg-surface px-2 py-0.5 text-[11px] font-semibold">
          {KI_LOESUNG_ZUSTAND_LABELS[f.zustand]}
        </span>
        <span className="text-[11px] text-muted">
          freigegeben von {f.markiertVonName} am{' '}
          {new Date(f.markiertAm).toLocaleDateString('de-DE')}
          {f.personenbezug ? ' · mit personenbezogenen Daten' : ' · ohne Kundendaten'}
        </span>
        <span className="ml-auto flex gap-1">
          <button
            type="button"
            onClick={() => setDialog(true)}
            className="cursor-pointer text-[11px] text-ink2 underline"
          >
            Auftrag ändern
          </button>
          <button
            type="button"
            disabled={zuruecknehmen.isPending}
            onClick={() => {
              setFehler(null)
              zuruecknehmen.mutate()
            }}
            className="cursor-pointer text-[11px] text-ink2 underline"
          >
            Freigabe zurücknehmen
          </button>
        </span>
      </div>

      {f.auftrag && (
        <p className="mt-1.5 text-ink2">
          <strong>Auftrag:</strong> {f.auftrag}
        </p>
      )}
      {f.personenbezug && f.einwilligungGrund && (
        <p className="mt-1 text-[11.5px] text-muted">Grund der Freigabe: {f.einwilligungGrund}</p>
      )}

      {f.ergebnis && (
        <div className="mt-2 rounded-lg border border-line bg-surface p-2.5">
          <div className="mb-1 text-[11px] font-semibold text-muted">
            Ergebnis von Claude · {new Date(f.zustandAm).toLocaleString('de-DE')}
          </div>
          <Markdown text={f.ergebnis} kompakt />
        </div>
      )}
      {f.mrUrl && (
        <p className="mt-1.5">
          Merge Request:{' '}
          <a href={f.mrUrl} target="_blank" rel="noreferrer" className="text-accent underline">
            {f.mrUrl}
          </a>
        </p>
      )}

      {stand.entwuerfe.map((e) => (
        <Entwurf key={e.id} entwurf={e} fertig={neuLaden} beiFehler={setFehler} />
      ))}

      {fehler && <p className="mt-2 text-[12px] text-pool-sofort">{fehler}</p>}

      {dialog && (
        <FreigabeDialog
          taskId={taskId}
          vorher={{
            personenbezug: f.personenbezug,
            grund: f.einwilligungGrund ?? '',
            auftrag: f.auftrag ?? '',
          }}
          schliessen={() => setDialog(false)}
          fertig={() => {
            setDialog(false)
            neuLaden()
          }}
        />
      )}
    </section>
  )
}

function Entwurf({
  entwurf,
  fertig,
  beiFehler,
}: {
  entwurf: { id: string; text: string; erstelltAm: Date | string }
  fertig: () => void
  beiFehler: (text: string | null) => void
}) {
  const [text, setText] = useState(entwurf.text)
  const abschicken = useMutation({
    mutationFn: () =>
      trpc.claude.entwurfUebernehmen.mutate({
        id: entwurf.id,
        ...(text.trim() !== entwurf.text ? { text: text.trim() } : {}),
      }),
    onSuccess: fertig,
    onError: (e: Error) => beiFehler(e.message),
  })
  const verwerfen = useMutation({
    mutationFn: () => trpc.claude.entwurfVerwerfen.mutate({ id: entwurf.id }),
    onSuccess: fertig,
    onError: (e: Error) => beiFehler(e.message),
  })

  return (
    <div className="mt-2 rounded-lg border border-dashed border-line2 bg-surface p-2.5">
      <div className="mb-1 text-[11px] font-semibold text-muted">
        Kommentar-Entwurf von Claude · {new Date(entwurf.erstelltAm).toLocaleString('de-DE')} · noch
        nicht verschickt
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={Math.min(12, Math.max(3, text.split('\n').length + 1))}
        aria-label="Entwurf bearbeiten"
        className="w-full rounded-lg border border-line2 bg-surface px-2 py-1.5 text-[12.5px]"
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <Button
          size="sm"
          disabled={abschicken.isPending || text.trim() === ''}
          onClick={() => {
            beiFehler(null)
            abschicken.mutate()
          }}
        >
          Als meinen Kommentar abschicken
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={verwerfen.isPending}
          onClick={() => {
            beiFehler(null)
            verwerfen.mutate()
          }}
        >
          Verwerfen
        </Button>
        <span className="text-[11px] text-muted">
          Geht unter deinem Namen in die Quelle – bei NetCon-Tickets kann der Kunde ihn sehen.
        </span>
      </div>
    </div>
  )
}

/**
 * **Die Einwilligung** – Nils, 16.10.2026: *„da muss vorher ein popup mit einwilligung und grund
 * kommen (falls überhaupt personenbezogene kundendaten im ticket sind, 90% der fälle sind
 * entwicklungsreviews)"*.
 *
 * Nichts ist vorausgewählt: die Frage wird jedes Mal beantwortet. „Nein" ist ein Klick, „Ja"
 * verlangt Grund und Häkchen – sonst bleibt „Freigeben" grau.
 */
function FreigabeDialog({
  taskId,
  vorher,
  schliessen,
  fertig,
}: {
  taskId: string
  vorher?: { personenbezug: boolean; grund: string; auftrag: string }
  schliessen: () => void
  fertig: () => void
}) {
  const [personenbezug, setPersonenbezug] = useState<boolean | null>(vorher?.personenbezug ?? null)
  const [grund, setGrund] = useState(vorher?.grund ?? '')
  const [einwilligung, setEinwilligung] = useState(Boolean(vorher?.personenbezug))
  const [auftrag, setAuftrag] = useState(vorher?.auftrag ?? '')
  const [fehler, setFehler] = useState<string | null>(null)

  const freigeben = useMutation({
    mutationFn: () =>
      trpc.claude.freigeben.mutate({
        taskId,
        personenbezug: personenbezug === true,
        ...(personenbezug ? { grund: grund.trim() } : {}),
        ...(auftrag.trim() ? { auftrag: auftrag.trim() } : {}),
      }),
    onSuccess: fertig,
    onError: (e: Error) => setFehler(e.message),
  })

  const bereit =
    personenbezug === false ||
    (personenbezug === true && einwilligung && grund.trim().length >= KI_GRUND_MIN)

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-[60] flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[12vh]"
      {...hintergrundSchliesst(schliessen)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation()
          schliessen()
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Für Claude freigeben"
    >
      <div className="w-full min-w-0 max-w-[520px] rounded-card border border-line bg-surface p-[18px] text-[12.5px] shadow-card">
        <h3 className="text-sm font-bold">🤖 Für Claude freigeben</h3>
        <p className="mt-1 text-ink2">
          Claude bekommt über die Anbindung Titel, Beschreibung, Verlauf und alle Kommentare dieser
          Aufgabe zu lesen (verarbeitet bei Anthropic). Claude kann hier nur einen Stand melden und
          Kommentar-Entwürfe anlegen, nichts verschicken.
        </p>

        <fieldset className="mt-3">
          <legend className="font-semibold">
            Enthält die Aufgabe personenbezogene Kundendaten?
          </legend>
          <label className="mt-1.5 flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name="personenbezug"
              checked={personenbezug === false}
              onChange={() => setPersonenbezug(false)}
              className="mt-0.5"
            />
            <span>
              <strong>Nein</strong> – ein Entwicklungsthema, keine Namen, Adressen, Vertrags- oder
              Kontaktdaten von Kunden
            </span>
          </label>
          <label className="mt-1 flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name="personenbezug"
              checked={personenbezug === true}
              onChange={() => setPersonenbezug(true)}
              className="mt-0.5"
            />
            <span>
              <strong>Ja</strong> – die Aufgabe enthält Daten über Kunden
            </span>
          </label>
        </fieldset>

        {personenbezug && (
          <div className="mt-3 rounded-lg bg-[#fdf3dc] p-2.5 text-[#6b4b00]">
            <label className="block font-semibold" htmlFor="ki-grund">
              Warum muss Claude diese Aufgabe trotzdem sehen?
            </label>
            <textarea
              id="ki-grund"
              value={grund}
              onChange={(e) => setGrund(e.target.value)}
              rows={2}
              placeholder="z. B. Fehler tritt nur bei diesem Kundenvertrag auf, Ursache im Code gesucht"
              className="mt-1 w-full rounded-lg border border-line2 bg-surface px-2 py-1.5 text-ink"
            />
            <label className="mt-1.5 flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={einwilligung}
                onChange={(e) => setEinwilligung(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Ich willige ein, dass die personenbezogenen Daten dieser Aufgabe zu diesem Zweck an
                Claude übermittelt werden. Grund und Zeitpunkt werden festgehalten.
              </span>
            </label>
          </div>
        )}

        <label className="mt-3 block font-semibold" htmlFor="ki-auftrag">
          Auftrag an Claude <span className="font-normal text-muted">(optional)</span>
        </label>
        <textarea
          id="ki-auftrag"
          value={auftrag}
          onChange={(e) => setAuftrag(e.target.value)}
          rows={2}
          placeholder="z. B. Ursache finden und Fix als Merge Request vorschlagen"
          className="mt-1 w-full rounded-lg border border-line2 bg-surface px-2 py-1.5"
        />

        {fehler && <p className="mt-2 text-pool-sofort">{fehler}</p>}

        <div className="mt-3 flex justify-end gap-1.5">
          <Button variant="ghost" size="sm" onClick={schliessen}>
            Abbrechen
          </Button>
          <Button
            size="sm"
            disabled={!bereit || freigeben.isPending}
            onClick={() => {
              setFehler(null)
              freigeben.mutate()
            }}
          >
            Freigeben
          </Button>
        </div>
      </div>
    </div>
  )
}
